import { isValidElement } from 'react'
import { openPrompt } from './Container'
import type { IMessageBox } from './Prompt'

type IPayload = Record<string, any>

export type IPrompt<T> = {
  (props: IMessageBox): Promise<T>
  success: (
    content: ReactNode | IMessageBox,
    title?: ReactNode | IMessageBox,
    config?: IMessageBox,
  ) => Promise<T>
  warn: (
    content: ReactNode | IMessageBox,
    title?: ReactNode | IMessageBox,
    config?: IMessageBox,
  ) => Promise<T>
  warning: (
    content: ReactNode | IMessageBox,
    title?: ReactNode | IMessageBox,
    config?: IMessageBox,
  ) => Promise<T>
  error: (
    content: ReactNode | IMessageBox,
    title?: ReactNode | IMessageBox,
    config?: IMessageBox,
  ) => Promise<T>
  wrench: (
    content: ReactNode | IMessageBox,
    title?: ReactNode | IMessageBox,
    config?: IMessageBox,
  ) => Promise<T>
  offline: (
    content: ReactNode | IMessageBox,
    title?: ReactNode | IMessageBox,
    config?: IMessageBox,
  ) => Promise<T>
  info: (
    content: ReactNode | IMessageBox,
    title?: ReactNode | IMessageBox,
    config?: IMessageBox,
  ) => Promise<T>
  default: (
    content: ReactNode | IMessageBox,
    title?: ReactNode | IMessageBox,
    config?: IMessageBox,
  ) => Promise<T>
  payload: T
}

const handleArguments = (
  content: ReactNode | IMessageBox,
  title?: ReactNode | IMessageBox,
  config?: IMessageBox,
) => {
  const ifContentIsElement =
    isValidElement(content) || typeof content === 'string' || typeof content === 'number'
  const ifTitleIsElement =
    isValidElement(title) || typeof title === 'string' || typeof title === 'number'
  let props = {} as IMessageBox
  if (ifContentIsElement && !title) props = { content } as IMessageBox
  if (typeof content === 'object' && !ifContentIsElement) props = content as IMessageBox
  if (typeof title === 'object' && ifContentIsElement && !ifTitleIsElement)
    props = { ...title, content } as IMessageBox
  if (typeof config === 'object' && ifContentIsElement && ifTitleIsElement)
    props = { ...config, title, content } as IMessageBox
  props.type = props.type || 'warning'
  return props
}

const Prompt: IPrompt<IPayload> = (props) => {
  return new Promise<IPayload>((resolve, reject) => {
    openPrompt({
      ...props,
      payload: Prompt.payload,
      promise: { resolve, reject },
      onUnMount() {
        Promise.resolve().then(() => {
          Prompt.payload = {}
        })
      },
    })
  })
}
Prompt.success = () => Promise.resolve({})
Prompt.error = () => Promise.resolve({})
Prompt.info = () => Promise.resolve({})
Prompt.warn = () => Promise.resolve({})
Prompt.offline = () => Promise.resolve({})
Prompt.wrench = () => Promise.resolve({})
Prompt.warning = () => Promise.resolve({})
Prompt.default = () => Promise.resolve({})
Prompt.payload = {}

const types: IMessageBox['type'][] = [
  'error',
  'info',
  'offline',
  'success',
  'warning',
  'wrench',
  'default',
]

types.forEach((type) => {
  Prompt[type] = (...args) => {
    const props = handleArguments(...args)
    props.type = type
    return Prompt(props)
  }
})

Prompt.warn = Prompt.warning

export default Prompt
